/**
 * AmbassadorMyProperties — Property pipeline kanban + list view
 *
 * Shows all ambassador_properties grouped by pipeline_status.
 * Desktop: kanban columns. Mobile: filterable card list.
 * Notes drawer per property card.
 *
 * URL: /ambassador/properties
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import DataTable, { StatusBadge } from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import { POS_GOLD } from '../../config/posRoomStatus';


interface AmbassadorProperty {
  id: string;
  property_name: string;
  property_type: string | null;
  city: string | null;
  province: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  pipeline_status: string;
  last_activity_at: string;
  rejection_reason: string | null;
  commission_rate: number;
  hosp_property_id: string | null;
  created_at: string;
}

interface Note {
  id: string;
  note_text: string;
  created_at: string;
}

const PIPELINE_COLUMNS = [
  { key: 'new', label: 'New', color: '#6b7280' },
  { key: 'in_progress', label: 'In Progress', color: '#1d4ed8' },
  { key: 'active', label: 'Active', color: '#16a34a' },
  { key: 'expired', label: 'Expired', color: '#ea580c' },
  { key: 'lost', label: 'Lost', color: '#dc2626' },
] as const;

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function AmbassadorMyProperties() {
  const { supabase, user } = useAuth();
  const [properties, setProperties] = useState<AmbassadorProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'board'>(() => {
    return (localStorage.getItem('ambassadorPropertiesViewMode') as 'list' | 'board') || 'board';
  });
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Notes state
  const [activeNotePropertyId, setActiveNotePropertyId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Status change
  const [changingStatus, setChangingStatus] = useState<string | null>(null);

  // Key the load on the user's id, not the user object. AuthContext returns a
  // new user object on every token refresh / tab refocus; keying on the object
  // reloaded the page each time.
  const userId = user?.id;
  const loadData = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase
      .from('ambassador_properties')
      .select('id, property_name, property_type, city, province, contact_name, contact_email, contact_phone, pipeline_status, last_activity_at, rejection_reason, commission_rate, hosp_property_id, created_at')
      .eq('user_id', userId)
      .order('last_activity_at', { ascending: false });

    setProperties((data || []) as AmbassadorProperty[]);
    setLoading(false);
  }, [supabase, userId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { localStorage.setItem('ambassadorPropertiesViewMode', viewMode); }, [viewMode]);

  // ── Notes ──────────────────────────────────────────────
  const openNotes = async (propertyId: string) => {
    if (activeNotePropertyId === propertyId) {
      setActiveNotePropertyId(null);
      return;
    }
    setActiveNotePropertyId(propertyId);
    setNotesLoading(true);
    setNewNote('');

    const { data } = await supabase.functions.invoke('ambassador-lead-manage', {
      body: { action: 'list_notes', id: propertyId },
    });

    setNotes((data?.notes || []) as Note[]);
    setNotesLoading(false);
  };

  const addNote = async (propertyId: string) => {
    if (!newNote.trim()) return;
    setAddingNote(true);

    const { data } = await supabase.functions.invoke('ambassador-lead-manage', {
      body: { action: 'add_note', id: propertyId, note_text: newNote.trim() },
    });

    if (data?.success) {
      setNotes(prev => [data.note as Note, ...prev]);
      setNewNote('');
      // Update last_activity_at locally
      setProperties(prev => prev.map(p =>
        p.id === propertyId ? { ...p, last_activity_at: new Date().toISOString() } : p
      ));
    }
    setAddingNote(false);
  };

  // ── Status change ──────────────────────────────────────
  const changeStatus = async (propertyId: string, newStatus: string) => {
    setChangingStatus(propertyId);

    const { data } = await supabase.functions.invoke('ambassador-lead-manage', {
      body: { action: 'update', id: propertyId, pipeline_status: newStatus },
    });

    if (data?.success) {
      setProperties(prev => prev.map(p =>
        p.id === propertyId ? { ...p, pipeline_status: newStatus, last_activity_at: new Date().toISOString() } : p
      ));
    }
    setChangingStatus(null);
  };

  // ── Render helpers ─────────────────────────────────────
  const getNextStatuses = (current: string): { key: string; label: string }[] => {
    switch (current) {
      case 'new': return [{ key: 'in_progress', label: 'Start Working' }, { key: 'lost', label: 'Mark Lost' }];
      case 'in_progress': return [{ key: 'lost', label: 'Mark Lost' }];
      case 'active': return []; // HQ-controlled
      case 'expired': return [{ key: 'in_progress', label: 'Re-engage' }];
      case 'lost': return [{ key: 'in_progress', label: 'Reopen' }];
      default: return [];
    }
  };

  const renderCard = (prop: AmbassadorProperty) => {
    const isNotesOpen = activeNotePropertyId === prop.id;
    const nextStatuses = getNextStatuses(prop.pipeline_status);
    const colCfg = PIPELINE_COLUMNS.find(c => c.key === prop.pipeline_status);

    return (
      <div key={prop.id} className="ops-board-card" style={{ cursor: 'default' }}>
        {/* Header */}
        <div className="flex justify-between items-start">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 'var(--text-md)' }}>{prop.property_name}</div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', marginTop: '2px' }}>
              {[prop.property_type, prop.city].filter(Boolean).join(' \u00b7 ')}
            </div>
          </div>
          {prop.pipeline_status === 'active' && (
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: POS_GOLD, whiteSpace: 'nowrap', marginLeft: '8px' }}>
              {prop.commission_rate}%
            </span>
          )}
        </div>

        {/* Contact */}
        {prop.contact_name && (
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', marginTop: '4px' }}>
            {prop.contact_name}{prop.contact_phone ? ` \u00b7 ${prop.contact_phone}` : ''}
          </div>
        )}

        {/* Last activity */}
        <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-border)', marginTop: '4px' }}>
          {timeAgo(prop.last_activity_at)}
        </div>

        {/* Rejection reason */}
        {prop.rejection_reason && prop.pipeline_status === 'in_progress' && (
          <div style={{
            marginTop: '6px', padding: '6px 8px', background: '#fef2f2',
            borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-xs)', color: 'var(--color-error)',
          }}>
            HQ: {prop.rejection_reason}
          </div>
        )}

        {/* Mobile status badge (shown in list view) */}
        <div className="ambassador-mobile-badge mt-sm">
          {colCfg && (
            <StatusBadge bg={`${colCfg.color}18`} color={colCfg.color}>{colCfg.label}</StatusBadge>
          )}
        </div>

        {/* Actions */}
        {nextStatuses.length > 0 && (
          <div style={{ display: 'flex', gap: '4px', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
            {nextStatuses.map(ns => (
              <button
                key={ns.key}
                onClick={() => changeStatus(prop.id, ns.key)}
                disabled={changingStatus === prop.id}
                style={{
                  flex: 1, padding: '5px 8px', fontSize: 'var(--text-xs)', fontWeight: 600,
                  border: '1px solid #e5e7eb', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  background: ns.key === 'lost' ? 'var(--color-white)' : '#f0f7ff',
                  color: ns.key === 'lost' ? 'var(--color-text-light)' : '#1d4ed8',
                }}
              >
                {changingStatus === prop.id ? '...' : ns.label}
              </button>
            ))}
          </div>
        )}

        {/* Notes toggle */}
        <button
          onClick={() => openNotes(prop.id)}
          style={{
            width: '100%', padding: '5px', marginTop: '6px', fontSize: 'var(--text-xs)',
            color: 'var(--color-text-light)', background: 'none', border: 'none', cursor: 'pointer',
            textAlign: 'center',
          }}
        >
          {isNotesOpen ? '\u25B2 Hide Notes' : '\u25BC Notes'}
        </button>

        {/* Notes drawer */}
        {isNotesOpen && (
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}>
            {/* Add note */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
              <input
                type="text"
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addNote(prop.id); }}
                placeholder="Add a note..."
                style={{
                  flex: 1, padding: '6px 8px', fontSize: 'var(--text-sm)', border: '1px solid #e5e7eb',
                  borderRadius: 'var(--radius-sm)', outline: 'none',
                }}
              />
              <button
                onClick={() => addNote(prop.id)}
                disabled={addingNote || !newNote.trim()}
                style={{
                  padding: '6px 12px', fontSize: 'var(--text-xs)', fontWeight: 600,
                  background: POS_GOLD, color: 'var(--color-sidebar)', border: 'none',
                  borderRadius: 'var(--radius-sm)', cursor: newNote.trim() ? 'pointer' : 'not-allowed',
                  opacity: newNote.trim() ? 1 : 0.5,
                }}
              >
                {addingNote ? '...' : 'Add'}
              </button>
            </div>

            {/* Notes list */}
            {notesLoading ? (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textAlign: 'center', padding: '8px' }}>Loading...</div>
            ) : notes.length === 0 ? (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textAlign: 'center', padding: '8px' }}>No notes yet</div>
            ) : (
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {notes.map(note => (
                  <div key={note.id} style={{ padding: '4px 0', borderBottom: '1px solid #f8f8f8', fontSize: 'var(--text-sm)' }}>
                    <div style={{ color: 'var(--color-sidebar)' }}>{note.note_text}</div>
                    <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-border)', marginTop: '2px' }}>{timeAgo(note.created_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <LoadingSpinner />;

  // Group properties by status
  const grouped = PIPELINE_COLUMNS.reduce((acc, col) => {
    acc[col.key] = properties.filter(p => p.pipeline_status === col.key);
    return acc;
  }, {} as Record<string, AmbassadorProperty[]>);

  // Filtered list for list view
  const filteredProperties = statusFilter === 'all'
    ? properties
    : properties.filter(p => p.pipeline_status === statusFilter);

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex justify-between items-start mb-lg gap-lg">
        <div className="flex-1">
          <h2 style={{ margin: 0, fontSize: 'var(--text-2xl)' }}>My Properties</h2>
          <p style={{ margin: '6px 0 0', fontSize: 'var(--text-md)', color: 'var(--color-text-light)', lineHeight: 1.5 }}>
            An optional contact book. Use it as a lightweight place to note properties you are following up with — add a card, jot a phone number, drop a note. Skip it entirely if you prefer your own diary or CRM. The real action happens on <strong>My Proposals</strong>; this page is just for your own reference.
          </p>
        </div>
        <Link
          to="/ambassador/submit-property"
          style={{
            padding: '8px 16px', background: POS_GOLD, color: 'var(--color-sidebar)',
            borderRadius: 'var(--radius-md)', textDecoration: 'none', fontWeight: 600, fontSize: 'var(--text-md)',
            whiteSpace: 'nowrap',
          }}
        >
          + Add Property
        </Link>
      </div>

      {/* View toggle — List / Board */}
      <div className="page-tabs mb-lg">
        <button
          className={`page-tab ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
        >
          List
        </button>
        <button
          className={`page-tab ${viewMode === 'board' ? 'active' : ''}`}
          onClick={() => setViewMode('board')}
        >
          Board
        </button>
      </div>

      {/* Summary strip */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '4px' }}>
        {PIPELINE_COLUMNS.map(col => {
          const count = grouped[col.key]?.length || 0;
          return (
            <div
              key={col.key}
              style={{
                padding: '6px 12px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--text-xs)', fontWeight: 600,
                whiteSpace: 'nowrap', background: `${col.color}12`, color: col.color,
                border: `1px solid ${col.color}30`,
              }}
            >
              {col.label}: {count}
            </div>
          );
        })}
      </div>

      {viewMode === 'list' ? (
        <>
          {/* Status filter pills */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', overflowX: 'auto', paddingBottom: '4px' }}>
            <button
              onClick={() => setStatusFilter('all')}
              style={{
                padding: '6px 12px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--text-xs)', fontWeight: 600,
                border: statusFilter === 'all' ? `2px solid ${POS_GOLD}` : '1px solid #e5e7eb',
                background: statusFilter === 'all' ? '#fefce8' : 'var(--color-white)', color: 'var(--color-sidebar)',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              All ({properties.length})
            </button>
            {PIPELINE_COLUMNS.map(col => {
              const count = grouped[col.key]?.length || 0;
              if (count === 0) return null;
              return (
                <button
                  key={col.key}
                  onClick={() => setStatusFilter(col.key)}
                  style={{
                    padding: '6px 12px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--text-xs)', fontWeight: 600,
                    border: statusFilter === col.key ? `2px solid ${col.color}` : '1px solid #e5e7eb',
                    background: statusFilter === col.key ? `${col.color}12` : '#fff', color: col.color,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  {col.label} ({count})
                </button>
              );
            })}
          </div>

          {/* DataTable list view */}
          {filteredProperties.length === 0 ? (
            <EmptyState
              message={statusFilter === 'all' ? 'No properties yet. Add your first one above.' : 'No properties in this stage.'}
            />
          ) : (
            <DataTable
              data={filteredProperties.map(p => ({
                id: p.id,
                property_name: p.property_name,
                city: p.city || '-',
                contact_name: p.contact_name || '-',
                pipeline_status: p.pipeline_status,
                commission_rate: p.pipeline_status === 'active' ? `${p.commission_rate}%` : '-',
                last_activity: timeAgo(p.last_activity_at),
              }))}
              columns={[
                { key: 'property_name', label: 'Property' },
                { key: 'city', label: 'City' },
                { key: 'contact_name', label: 'Contact' },
                { key: 'pipeline_status', label: 'Status', render: (row: Record<string, unknown>) => {
                  const colCfg = PIPELINE_COLUMNS.find(c => c.key === row.pipeline_status);
                  return colCfg
                    ? <StatusBadge bg={`${colCfg.color}18`} color={colCfg.color}>{colCfg.label}</StatusBadge>
                    : <span>{row.pipeline_status as string}</span>;
                }},
                { key: 'commission_rate', label: 'Rate' },
                { key: 'last_activity', label: 'Last Activity' },
              ]}
            />
          )}
        </>
      ) : (
        /* Board view — Kanban columns */
        <div className="ops-board">
          {PIPELINE_COLUMNS.map(col => (
            <div key={col.key} className="ops-board-column">
              <div className="ops-board-column-header" style={{ borderTopColor: col.color }}>
                <span style={{ fontWeight: 600, fontSize: 'var(--text-md)' }}>{col.label}</span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)' }}>{grouped[col.key]?.length || 0}</span>
              </div>
              <div className="ops-board-column-body">
                {(grouped[col.key] || []).length === 0 ? (
                  <div style={{ padding: '16px', textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--color-border)' }}>
                    No properties
                  </div>
                ) : (
                  (grouped[col.key] || []).map(prop => renderCard(prop))
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AmbassadorMyProperties;
